import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Dispatch fee paid out per settlement, attributed to whichever staff
// member dispatched that load (Load.created_by -> StaffProfile). The fee
// amount itself already exists on Settlement -- this just answers "which
// dispatcher generated it."
export default function DispatcherSettlementsReport() {
  const [settlements, setSettlements] = useState([]);
  const [loads, setLoads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [dispatcherFilter, setDispatcherFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Settlement.list('-settlement_date', 500),
      client.entities.Load.list('-delivery_date', 500),
      client.entities.StaffProfile.list('-created_date', 200),
    ]).then(([s, l, st]) => { setSettlements(s); setLoads(l); setStaff(st); setLoading(false); });
  }, []);

  const staffNameByUserId = new Map(staff.map(s => [s.user_id, s.full_name || s.user_email]));
  const loadById = new Map(loads.map(l => [l.id, l]));

  const rows = settlements
    .filter(s => inRange(s.settlement_date || s.finalized_date || s.created_date, start, end) && (s.dispatch_fee_amount || 0) > 0)
    .map(s => {
      const l = loadById.get(s.load_id);
      const dispatcher = l?.created_by ? (staffNameByUserId.get(l.created_by) || 'Unknown') : 'Unassigned';
      return {
        Dispatcher: dispatcher,
        'Load #': s.load_number || '',
        'Delivery Date': l?.delivery_date || '',
        'Gross Revenue': s.gross_revenue || 0,
        'Dispatch Fee': s.dispatch_fee_amount || 0,
      };
    })
    .filter(r => !dispatcherFilter || r.Dispatcher === dispatcherFilter);

  const dispatchers = [...new Set(rows.map(r => r.Dispatcher))].sort();
  const totalFees = rows.reduce((s, r) => s + r['Dispatch Fee'], 0);

  const csvRows = rows.map(r => ({ ...r, 'Gross Revenue': r['Gross Revenue'].toFixed(2), 'Dispatch Fee': r['Dispatch Fee'].toFixed(2) }));

  const handlePrint = () => {
    const cols = ['Dispatcher', 'Load #', 'Delivery Date', 'Gross Revenue', 'Dispatch Fee'];
    const dataRows = rows.map(r => [
      { value: r.Dispatcher, class: 'bold' }, `#${r['Load #']}`, r['Delivery Date'] || '—',
      { value: `$${r['Gross Revenue'].toLocaleString()}`, class: '' },
      { value: `$${r['Dispatch Fee'].toFixed(2)}`, class: 'gold' },
    ]);
    const totalsRow = ['TOTAL', '', '', '', { value: `$${totalFees.toFixed(2)}`, class: 'gold' }];
    const cards = [
      { label: 'Dispatchers', value: dispatchers.length, color: 'gold' },
      { label: 'Settlements', value: rows.length, color: '' },
      { label: 'Total Dispatch Fees', value: `$${totalFees.toFixed(2)}`, color: 'positive' },
    ];
    const html = generatePrintHTML('Dispatcher Settlements Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'dispatcher-settlements.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Dispatcher:</label>
        <select value={dispatcherFilter} onChange={e => setDispatcherFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
          <option value="" className="bg-background">All Dispatchers</option>
          {dispatchers.map(d => <option key={d} value={d} className="bg-background">{d}</option>)}
        </select>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Dispatcher', 'Load #', 'Delivery Date', 'Gross Revenue', 'Dispatch Fee'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Dispatcher}</td>
                <td className="px-3 py-2.5 text-primary">#{r['Load #']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Delivery Date'] || '—'}</td>
                <td className="px-3 py-2.5 text-foreground">${r['Gross Revenue'].toLocaleString()}</td>
                <td className="px-3 py-2.5 font-bold text-primary">${r['Dispatch Fee'].toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5 font-bold">
                <td colSpan={4} className="px-3 py-2.5 text-foreground">TOTAL</td>
                <td className="px-3 py-2.5 text-foreground">${totalFees.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No dispatch fees found for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">Dispatcher attributed via the load's original creator. Loads created before staff attribution was tracked show as "Unassigned".</p>
    </div>
  );
}
